import { Directive, ElementRef, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';

/**
 * Adds click-and-drag (mouse) horizontal scrolling to an element that already
 * scrolls via overflow-x, so desktop mouse users can drag it the same way
 * touch users can swipe it.
 */
@Directive({
  selector: '[ipmDragScroll]'
})
export class DragScrollDirective implements OnInit, OnDestroy {
  private isPointerDown = false;
  private hasDragged = false;
  private startX = 0;
  private startScrollLeft = 0;
  private readonly dragThreshold = 5;

  private moveListener?: (event: PointerEvent) => void;
  private upListener?: (event: PointerEvent) => void;
  private captureClickListener?: (event: MouseEvent) => void;

  constructor(private elementRef: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    // Capture phase so a post-drag click is swallowed before it reaches
    // child elements (playlist cards / tag chips) that have their own click handlers.
    this.captureClickListener = (event: MouseEvent) => {
      if (this.hasDragged) {
        event.preventDefault();
        event.stopPropagation();
        this.hasDragged = false;
      }
    };
    this.elementRef.nativeElement.addEventListener('click', this.captureClickListener, true);
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'touch' || event.button !== 0) {
      return;
    }
    const el = this.elementRef.nativeElement;
    this.isPointerDown = true;
    this.hasDragged = false;
    this.startX = event.clientX;
    this.startScrollLeft = el.scrollLeft;

    this.ngZone.runOutsideAngular(() => {
      this.moveListener = (moveEvent: PointerEvent) => this.onPointerMove(moveEvent);
      this.upListener = () => this.onPointerUp();
      window.addEventListener('pointermove', this.moveListener);
      window.addEventListener('pointerup', this.upListener);
      window.addEventListener('pointercancel', this.upListener);
    });
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.isPointerDown) {
      return;
    }
    const delta = event.clientX - this.startX;
    if (!this.hasDragged && Math.abs(delta) < this.dragThreshold) {
      return;
    }
    this.hasDragged = true;
    event.preventDefault();
    this.elementRef.nativeElement.scrollLeft = this.startScrollLeft - delta;
  }

  private onPointerUp(): void {
    this.isPointerDown = false;
    if (this.moveListener) {
      window.removeEventListener('pointermove', this.moveListener);
    }
    if (this.upListener) {
      window.removeEventListener('pointerup', this.upListener);
      window.removeEventListener('pointercancel', this.upListener);
    }
    this.moveListener = undefined;
    this.upListener = undefined;
  }

  ngOnDestroy(): void {
    this.onPointerUp();
    if (this.captureClickListener) {
      this.elementRef.nativeElement.removeEventListener('click', this.captureClickListener, true);
    }
  }
}
